import { useState, useRef } from "react";
import { FIRST_PAGE, MAX_ROWS, PAGINATION_INITIAL_STATE, PAGINATION_MODES } from "../HyroTable.consts";

export default function usePagination({
  showPagination,
  rows,
  autoNavigateToLastPage,
  apiRef,
  showInlineAddRow,
  paginationMode,
  rowCount: rowCountProp = 0,
  onPaginationModelChange,
  paginationModel: paginationModelProp,
}) {
  const [clientSidePaginationModel, setClientSidePaginationModel] = useState(
    showPagination ? PAGINATION_INITIAL_STATE : { page: FIRST_PAGE, pageSize: MAX_ROWS }
  );

  const paginationModel = paginationMode === PAGINATION_MODES.SERVER ? paginationModelProp : clientSidePaginationModel;

  const setPaginationModel = (model) => {
    if (paginationMode === PAGINATION_MODES.SERVER) {
      onPaginationModelChange(model);
    } else {
      setClientSidePaginationModel(model);
    }
  };

  const hasLoadedData = useRef(false);
  const prevRowIds = useRef(rows.map((row) => row.id));

  const displayedRows = paginationMode === PAGINATION_MODES.SERVER
    ? rows
    : (paginationModel.pageSize > 0 && showPagination
      ? rows.slice(
        paginationModel.page * paginationModel.pageSize,
        (paginationModel.page * paginationModel.pageSize) + paginationModel.pageSize
      )
      : rows);

  const rowCount = paginationMode === PAGINATION_MODES.SERVER ? rowCountProp : rows.length;

  const totalPages = Math.ceil(rowCount / paginationModel.pageSize);
  const isLastPage = paginationModel.page === totalPages - 1;

  const showAddButton = showInlineAddRow && (isLastPage || rows.length === 0);

  const handleRowCountChange = (newRowCount) => {
    if (!autoNavigateToLastPage) return;

    const currentRowIds = rows.map((row) => row.id);

    if (!hasLoadedData.current && newRowCount > 0) {
      prevRowIds.current = currentRowIds;
      hasLoadedData.current = true;
      return;
    }

    const previousRowIds = prevRowIds.current;
    const wasOnlyAdd =
      currentRowIds.length > previousRowIds.length && previousRowIds.every((id) => currentRowIds.includes(id));

    const lastPage = Math.floor((newRowCount - 1) / paginationModel.pageSize);

    if (wasOnlyAdd) {
      apiRef.current.setPage(lastPage);
    }

    prevRowIds.current = currentRowIds;
  };

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  return {
    paginationModel,
    setPaginationModel: handlePaginationModelChange,
    displayedRows,
    totalPages,
    isLastPage,
    showAddButton,
    pageSizeOptions: [25, 50, 75, 100],
    paginationInitialState: showPagination ? PAGINATION_INITIAL_STATE : {},
    onRowCountChange: (newRowCount) => handleRowCountChange(newRowCount),
    rowCount,
  };
}
